"use client";

import { ChevronDown, MessageCircle, X } from "lucide-react";
import { CommentEvents, trackEvent } from "@/analytics";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/shadcn/ui/drawer";

import { Comedian } from "@/data/comedians";
import { Comment } from "./CommentSection";
import { CommentAuthorComedian } from "@punchup/data/models";
import CommentInput from "./CommentInput";
import CommentList from "./CommentList";
import Image from "next/image";
import { SpecialRow } from "@/data/specials";
import { User } from "@supabase/supabase-js";
import { useState } from "react";

interface MobileCommentModalProps {
  comments: Comment[];
  currentUser?: User | null;
  currentUserId?: string;
  isLoading: boolean;
  total: number;
  hasMore: boolean;
  onSubmit: (
    content: string,
    parentId?: string,
    authorComedian?: CommentAuthorComedian | null
  ) => void;
  onReply: (
    content: string,
    parentId: string,
    replyingToUsername?: string,
    authorComedian?: CommentAuthorComedian | null
  ) => void;
  onClap: (commentId: string, clapCount: number) => Promise<void>;
  onEdit: (commentId: string, newContent: string) => void;
  onDelete: (commentId: string) => void;
  onLoadMore: () => void;
  onRefresh: () => void;
  isPosting: boolean;
  newCommentsAvailable: number;
  rateLimitError?: string | null;
  postError?: Error | null;
  error?: Error | null;
  // Tracking context
  special?: SpecialRow;
  comedian?: Comedian;
  isLoggedIn?: boolean;
  userFollowsComedian?: boolean;
}

export default function MobileCommentModal({
  comments,
  currentUser,
  currentUserId,
  isLoading,
  total,
  hasMore,
  onSubmit,
  onReply,
  onClap,
  onEdit,
  onDelete,
  onLoadMore,
  onRefresh,
  isPosting,
  newCommentsAvailable,
  rateLimitError,
  postError,
  error,
  special,
  comedian,
  isLoggedIn,
  userFollowsComedian,
}: MobileCommentModalProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Track modal open/close events
  const handleModalToggle = (open: boolean) => {
    if (special && comedian) {
      if (open) {
        trackEvent(CommentEvents.MOBILE_COMMENT_MODAL_OPENED, {
          specialId: special.id,
          comedianId: comedian.id,
          commentsCount: comments.length,
          total,
          isLoggedIn,
          userFollowsComedian,
        });
      } else {
        trackEvent(CommentEvents.MOBILE_COMMENT_MODAL_CLOSED, {
          specialId: special.id,
          comedianId: comedian.id,
          commentsCount: comments.length,
          total,
          isLoggedIn,
          userFollowsComedian,
        });
      }
    }
    setIsExpanded(open);
  };

  // Track preview click
  const handlePreviewClick = () => {
    if (special && comedian) {
      trackEvent(CommentEvents.MOBILE_COMMENT_PREVIEW_CLICKED, {
        specialId: special.id,
        comedianId: comedian.id,
        commentsCount: comments.length,
        total,
        hasTopComment: !!topComment,
        isLoggedIn,
        userFollowsComedian,
      });
    }
    handleModalToggle(true);
  };

  const getTopComment = () => {
    if (comments.length === 0) return null;
    // Return the first comment (assuming they're sorted by relevance/likes)
    return comments[0];
  };

  const topComment = getTopComment();

  // Get display info for the top comment, using current user metadata if it's their comment
  const getTopCommentDisplayInfo = (comment: Comment) => {
    const isCurrentUser = currentUser && comment.user_id === currentUser.id;

    if (isCurrentUser && currentUser.user_metadata) {
      const metadata = currentUser.user_metadata;
      return {
        // Prioritize email prefix to match profile URLs, but keep rich metadata for avatar
        displayName:
          currentUser.email?.split("@")[0] ||
          metadata.full_name ||
          metadata.name ||
          "User",
        avatarUrl: metadata.avatar_url || metadata.picture || null,
      };
    }

    // Fallback to comment data for other users
    return {
      displayName: comment.users_extra?.username || "User",
      avatarUrl: null,
    };
  };

  return (
    <>
      {/* Comment Preview/Trigger */}
      <div className="lg:hidden relative z-10">
        <div className="md:px-20 xl:px-0 xl:mx-auto max-w-7xl bg-white">
          {/* Outlined comment preview container */}
          <div
            onClick={handlePreviewClick}
            className="bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-200 rounded-lg p-3 cursor-pointer transition-colors ease-in-out duration-200 w-full"
          >
            {/* Header with Comments title and count */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex flex-row gap-x-1.5 items-center">
                <MessageCircle className="w-5 h-5 text-gray-600 flex-shrink-0" />
                <span className="text-base font-medium text-gray-900 leading-tight">
                  Comments
                </span>
              </div>
              <div className="flex items-center space-x-2 -mr-0.5">
                {total > 0 && (
                  <span className="text-sm text-gray-500">{total}</span>
                )}
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* Icon + Avatar + Content row */}
            {topComment ? (
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {(() => {
                    const { displayName, avatarUrl } =
                      getTopCommentDisplayInfo(topComment);
                    return avatarUrl ? (
                      <Image
                        src={avatarUrl}
                        alt={displayName}
                        width={32}
                        height={32}
                        className="rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 truncate">
                    {topComment.content}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <MessageCircle className="w-5 h-5 text-gray-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Share your thoughts</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drawer with smooth animations */}
      <div className="lg:hidden">
        <Drawer open={isExpanded} onOpenChange={handleModalToggle}>
          <DrawerContent className="max-h-[80vh] flex flex-col">
            {/* Header */}
            <DrawerHeader className="flex flex-row items-center justify-between pt-0 pb-2 px-4 border-b border-gray-200">
              <DrawerTitle className="text-lg font-semibold">
                Comments {total > 0 && `(${total})`}
              </DrawerTitle>
              <DrawerClose asChild>
                <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </DrawerClose>
            </DrawerHeader>

            {/* Refresh Button */}
            {newCommentsAvailable > 0 && (
              <div className="p-3 bg-blue-50 border-b border-gray-200">
                <button
                  onClick={onRefresh}
                  disabled={isLoading}
                  className="w-full px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {newCommentsAvailable} new comment
                  {newCommentsAvailable > 1 ? "s" : ""}
                </button>
              </div>
            )}

            {/* Error Messages */}
            {(error || postError || rateLimitError) && (
              <div className="p-3 bg-red-50 border-b border-red-200">
                <p className="text-sm text-red-800">
                  {rateLimitError ||
                    error?.message ||
                    postError?.message ||
                    "An error occurred"}
                </p>
              </div>
            )}

            {/* Comment Input */}
            <div className="p-3 border-b border-gray-200">
              <div className="border border-gray-200 rounded-lg px-3 py-2 md:py-3 focus-within:border-blue-500 focus-within:border-2 transition-all duration-200">
                <CommentInput
                  onSubmit={(content, authorComedian) =>
                    onSubmit(content, undefined, authorComedian)
                  }
                  placeholder="Add a comment..."
                  disabled={isPosting || !!rateLimitError}
                  currentUser={currentUser}
                  variant="outlined"
                  specialId={special?.id}
                  comedianId={comedian?.id}
                  isLoggedIn={isLoggedIn}
                  userFollowsComedian={userFollowsComedian}
                />
              </div>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto pb-12">
              {isLoading && comments.length === 0 ? (
                <div className="p-4 space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-2 animate-pulse">
                      <div className="flex items-center space-x-2">
                        <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                      </div>
                      <div className="h-16 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : comments.length === 0 ? (
                <div className="flex-1 flex items-center justify-center p-8">
                  <div className="text-center text-gray-500">
                    <p className="text-lg">Be the first to comment</p>
                    <p className="text-sm mt-2">Share your thoughts</p>
                  </div>
                </div>
              ) : (
                <div className="p-4">
                  <CommentList
                    comments={comments}
                    currentUserId={currentUserId}
                    currentUser={currentUser}
                    onReply={onReply}
                    onClap={onClap}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    specialId={special?.id}
                    comedianId={comedian?.id}
                    isLoggedIn={isLoggedIn}
                    userFollowsComedian={userFollowsComedian}
                  />
                  {hasMore && (
                    <div className="mt-6 text-center">
                      <button
                        onClick={onLoadMore}
                        disabled={isLoading}
                        className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? "Loading..." : "Load more comments"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </>
  );
}
